/**
 * The document content model.
 *
 * A document is a tree of numbered sections. Each section owns a rich-text
 * body stored as a ProseMirror-compatible JSON document, so the browser editor
 * can round-trip it losslessly while the server can serialise it to Markdown,
 * HTML, PDF or DOCX without a headless browser.
 *
 * Markdown is a *projection* of this model, never the source of truth. That
 * keeps documents machine readable and exportable while the authoring UI stays
 * free of Markdown syntax.
 */

export const CONTENT_SCHEMA_VERSION = 1 as const;

export type MarkType = 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'link' | 'highlight';

export interface Mark {
  type: MarkType;
  attrs?: Record<string, unknown> | null;
}

/** A ProseMirror-shaped node. `text` nodes carry `text`, everything else `content`. */
export interface RichNode {
  type: string;
  attrs?: Record<string, unknown> | null;
  content?: RichNode[];
  marks?: Mark[];
  text?: string;
}

export interface RichDoc extends RichNode {
  type: 'doc';
  content: RichNode[];
}

export interface Section {
  /** Stable across edits — cross references point at this. */
  id: string;
  title: string;
  /** URL/anchor slug, stable and unique within the document. */
  anchor: string;
  /** Whether the section takes part in clause numbering (1, 1.1, ...). */
  numbered: boolean;
  /** Author guidance from the template. Shown in the editor, never exported. */
  guidance?: string;
  /** Template-locked sections cannot be deleted or renamed by authors. */
  locked?: boolean;
  /** Optional: section is not rendered in exports (internal working notes). */
  internal?: boolean;
  body: RichDoc;
  children: Section[];
}

export interface DocumentContent {
  schemaVersion: typeof CONTENT_SCHEMA_VERSION;
  sections: Section[];
}

/** Metadata rendered as YAML front matter in Markdown exports. */
export interface DocumentFrontMatter {
  id?: string;
  reference?: string;
  title: string;
  type?: string;
  version?: string;
  status?: string;
  owner?: string;
  approver?: string;
  classification?: string;
  effective_from?: string;
  review_due?: string;
  tags?: string[];
  [key: string]: unknown;
}

export interface CrossReferenceAttrs {
  /** Target document id, or null when the reference is within this document. */
  documentId: string | null;
  /** Human-facing document reference code (e.g. POL-001), used in exports. */
  documentRef: string | null;
  /** Target section anchor, or null to reference the whole document. */
  sectionAnchor: string | null;
  /** Text shown to the reader. */
  label: string;
  /** External URL — set when this is a link out rather than an internal ref. */
  url: string | null;
}

/** A flattened section with its computed clause number and depth. */
export interface NumberedSection {
  section: Section;
  /** e.g. "1", "1.2", "1.2.3"; empty for unnumbered sections. */
  number: string;
  /** 1-based heading depth. */
  depth: number;
  path: string[];
}
